import React, {useRef, useState} from 'react';
import styles from './styles.module.scss';
import {
    Button,
    Flex,
    Icon,
    Input,
    PriorityIcon,
    Text
} from '@priority-software/priority-style-react';
import texts from '../texts';
import {
    ADD_QUANTITY_FIELD,
    DEFAULT_OPTIONAL_FIELDS,
    PARTS_FORM_NAME,
    PARTS_SEARCH_MONITOR,
    PARTS_SEARCH_MONITOR_SETTINGS,
    PARTS_SEARCH_MONITOR_SETTINGS_SAVE,
    SEARCH_BUTTON_NAME,
    SEARCH_INPUT_NAME
} from '../consts';
import {updateSelectedFields} from '../service';
import FamilySelect from './FamilySelect';
import {observer} from 'mobx-react';
import {partsStore} from '../PartsStore';
import {keys} from '../../../constants/KeyCodes';
import {useEventListener} from 'usehooks-ts';
import {EditingDialog} from '../../../components/EditingDialog';
import {useIsFetching} from 'react-query';
import {Tooltip} from '../../../components/Common/Tooltip';
import {formsStore} from '../../../_legacy/Form/stores/FormsStore';
import {sendMonitor} from '../../../utils/monitoring';

interface Props {
    query: string;
    formName: string;
    onChangeQuery: (value: string) => void;
    onSearch: () => void;
    hasTquant: boolean;
}

const Header = observer(({query, onChangeQuery, onSearch, formName, hasTquant}: Props) => {
    const {
        selectedColumns,
        editingColumns,
        setSelectedColumns,
        families,
        showQuantitySelector,
        setQuantitySelector,
        page,
        setSetSearchParams
    } = partsStore;
    const isFetching = useIsFetching();
    const [isSettingsPanelOpen, setIsSettingsPanelOpen] = useState(false);

    const menuRef = useRef<HTMLDivElement>();
    const inputRef = useRef<HTMLInputElement>();
    const mayDesignForm = formsStore?.currentForm?.parentForm?.maydesignform;
    const specialCheckboxText = texts.addQuantityField;

    const onKeyDown = (e: KeyboardEvent) => {
        if (document.activeElement === inputRef.current && e.key === keys.ENTER) {
            e.preventDefault();
            e.stopPropagation();
            onSearch();
            menuRef?.current?.focus();
        }
    };

    const openSettingsPanel = (e: KeyboardEvent) => {
        if (e.code === keys.B && e.ctrlKey && mayDesignForm) {
            e.preventDefault();
            e.stopPropagation();
            if (!isSettingsPanelOpen) {
                onSettingButtonClick();
            }
        }
    };

    const onSettingButtonClick = () => {
        setIsSettingsPanelOpen(true);
        sendMonitor(`${PARTS_SEARCH_MONITOR}${PARTS_SEARCH_MONITOR_SETTINGS}`);
    };

    const prepareSelectedFields = (fields: string[], formName: string) => {
        const quantitySelectorIndex = fields.indexOf(ADD_QUANTITY_FIELD);
        let shouldShowQuantitySelector = true;
        if (quantitySelectorIndex > -1) {
            fields.splice(quantitySelectorIndex, 1);
            setQuantitySelector(true);
        } else {
            shouldShowQuantitySelector = false;
            setQuantitySelector(false);
        }
        updateSelectedFields(fields, formName, shouldShowQuantitySelector);
    };

    useEventListener('keydown', openSettingsPanel);
    useEventListener('keyup', onKeyDown);

    return (
        <div ref={menuRef} className={styles.headerContainer} tabIndex={0}>
            <Flex justifyContent="flex-start" componentClasses={styles.headerSearch}>
                <Input
                    inputRef={inputRef}
                    name={SEARCH_INPUT_NAME}
                    autoFocus
                    placeholder={texts.searchPlaceholder}
                    value={query || ''}
                    componentClasses={styles.searchInput}
                    onChange={e => onChangeQuery(e.target.value)}
                >
                    <Flex inline>
                        {query && (
                            <Icon
                                icon={PriorityIcon.closeSmall}
                                onClick={() => onChangeQuery('')}
                            />
                        )}
                        <Icon icon={PriorityIcon.searchThin} />
                    </Flex>
                </Input>
                <Button disabled={isFetching > 0} onClick={onSearch} name={SEARCH_BUTTON_NAME}>
                    {texts.search}
                </Button>
            </Flex>
            <div className={styles.headerActions}>
                {families && (
                    <div className={styles.familySelectWrapper}>
                        <Text wrap={false} skin="label-1">
                            {texts.familyLabel}
                        </Text>
                        <FamilySelect />
                    </div>
                )}
                {mayDesignForm && (
                    <Tooltip tooltip={texts.settingToolTip} zIndex={10010}>
                        <Button
                            skin="secondary"
                            icon={<Icon icon={PriorityIcon.settings} />}
                            onClick={onSettingButtonClick}
                        />
                    </Tooltip>
                )}
                {isSettingsPanelOpen && (
                    <EditingDialog
                        closeDialog={() => setIsSettingsPanelOpen(false)}
                        defaultFields={DEFAULT_OPTIONAL_FIELDS}
                        selectedEntity={{
                            cols: editingColumns,
                            selectedFields: selectedColumns,
                            entityId: ''
                        }}
                        zoomFname={PARTS_FORM_NAME}
                        isOpen={isSettingsPanelOpen}
                        updateFields={(entityId, fields) => {
                            prepareSelectedFields(fields, formName);
                            setSelectedColumns(fields);
                            setIsSettingsPanelOpen(false);
                            setSetSearchParams({
                                page: page - 1,
                                selectedColumns: fields
                            });
                            sendMonitor(
                                `${PARTS_SEARCH_MONITOR}${PARTS_SEARCH_MONITOR_SETTINGS_SAVE}`
                            );
                        }}
                        sepcialCheckboxOpt={hasTquant ? ADD_QUANTITY_FIELD : null}
                        specialCheckboxState={hasTquant ? showQuantitySelector : null}
                        specialCheckboxText={hasTquant ? specialCheckboxText : null}
                        specialHelpText={hasTquant ? texts.quantityHelp : null}
                    />
                )}
            </div>
        </div>
    );
});

export default Header;
